import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, userProfileService } from '../services/userProfile';
import { authService } from '../services/auth';
import { notificationService, Notification as ApiNotification } from '../services/notifications';
import { chatService } from '../services/chat';
import DataExportModal from './DataExportModal';
import RocketLogo from './RocketLogo';
import {
  RefreshCwIcon,
  CheckIcon,
  FlameIcon,
  PartyIcon,
  BellIcon,
  DollarSignIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  WalletIcon,
  ClockIcon,
  BarChartIcon,
  MailIcon,
} from './Icons';
import './Header.css';

interface HeaderProps {
  isLoggedIn: boolean;
  userName?: string;
  onLogin: () => void;
  onLogout: () => void;
  onNavigate?: (viewId: string) => void;
  onToggleSidebar?: () => void;
}

interface Notification {
  id: string;
  type: 'routine' | 'task' | 'streak' | 'system' | 'achievement' | 'budget' | 'approval' | 'alert' | 'reserve' | 'savings' | 'reminder' | 'summary';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
}

const Header: React.FC<HeaderProps> = ({ isLoggedIn, userName, onLogin, onLogout, onNavigate, onToggleSidebar }) => {
  const [showDataExportModal, setShowDataExportModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoggedIn) {
      loadUserProfile();
      loadNotifications();
      
      // Set up auto-refresh for notifications every 30 seconds
      const interval = setInterval(() => {
        loadNotifications();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showDropdown || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, showNotifications]);

  const loadUserProfile = async () => {
    try {
      let profile = await userProfileService.getCurrentUser();
      if (!profile) {
        // Create default profile if none exists
        profile = await userProfileService.createDefaultProfile('user@example.com', 'demo');
      }
      setUserProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      // Load all notifications from unified endpoint
      const apiNotifications = await notificationService.getNotifications(false);
      
      // Convert API notifications to component format
      const convertedNotifications: Notification[] = apiNotifications.map((notif: ApiNotification) => ({
        id: notif.id,
        type: notif.type as Notification['type'],
        title: notif.title,
        message: notif.body,
        timestamp: new Date(notif.createdAt),
        read: !!notif.readAt,
        actionUrl: notif.link,
      }));

      // Sort by timestamp (newest first)
      const sortedNotifications = convertedNotifications.sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
      );

      setNotifications(sortedNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Fallback to empty array on error
      setNotifications([]);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      );

      // Mark notification as read on the backend
      try {
        await notificationService.markAsRead(notification.id);
      } catch (error) {
        console.error('Error marking notification as read:', error);
        // Revert the optimistic update on error
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, read: false } : n)
        );
      }
    }
    
    // Navigate if action URL exists
    if (notification.actionUrl && onNavigate) {
      // Handle Budget OS links that might be full URLs or relative paths
      if (notification.actionUrl.startsWith('http')) {
        window.location.href = notification.actionUrl;
      } else {
        // Handle relative paths - extract the view ID
        const pathParts = notification.actionUrl.split('/').filter(p => p);
        if (pathParts.length > 0) {
          // Get the last part as view ID, or handle Budget OS specific routes
          const lastPart = pathParts[pathParts.length - 1];
          // Map common Budget OS routes
          if (lastPart === 'budget-os' || pathParts.includes('budget-os')) {
            onNavigate('analytics'); // Budget OS dashboard is now in Analytics
          } else {
            onNavigate(lastPart);
          }
        }
      }
    }
    setShowNotifications(false);
  };

  const markAllAsRead = async () => {
    // Optimistically update UI
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    
    // Mark all notifications as read on backend
    try {
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Reload notifications to get correct state
      loadNotifications();
    }
  };

  const formatNotificationTime = (timestamp: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return timestamp.toLocaleDateString();
  };

  const getNotificationIcon = (type: Notification['type']): React.ReactNode => {
    const iconSize = 16;
    switch (type) {
      case 'routine': return <RefreshCwIcon size={iconSize} />;
      case 'task': return <CheckIcon size={iconSize} />;
      case 'streak': return <FlameIcon size={iconSize} />;
      case 'achievement': return <PartyIcon size={iconSize} />;
      case 'system': return <BellIcon size={iconSize} />;
      case 'budget': return <DollarSignIcon size={iconSize} />;
      case 'approval': return <CheckCircleIcon size={iconSize} />;
      case 'alert': return <AlertTriangleIcon size={iconSize} />;
      case 'reserve': return <WalletIcon size={iconSize} />;
      case 'savings': return <DollarSignIcon size={iconSize} />;
      case 'reminder': return <ClockIcon size={iconSize} />;
      case 'summary': return <BarChartIcon size={iconSize} />;
      default: return <MailIcon size={iconSize} />;
    }
  };

  // Listen for profile updates from the ProfilePage
  useEffect(() => {
    const handleProfileUpdate = (e: CustomEvent<UserProfile>) => {
      setUserProfile(e.detail);
    };
    window.addEventListener('profile-updated', handleProfileUpdate as EventListener);
    return () => window.removeEventListener('profile-updated', handleProfileUpdate as EventListener);
  }, []);

  const handleLogoutClick = async () => {
    try {
      await authService.logout();
      setUserProfile(null);
      setShowDropdown(false);
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if backend logout fails
      setUserProfile(null);
      setShowDropdown(false);
      onLogout();
    }
  };

  const handleExportData = () => {
    setShowDropdown(false);
    setShowDataExportModal(true);
  };

  const getDisplayName = () => {
    if (userProfile?.displayName) return userProfile.displayName;
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName}`;
    }
    return userName || 'User';
  };

  const getInitials = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName[0]}${userProfile.lastName[0]}`.toUpperCase();
    }
    const name = getDisplayName();
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <header className="header">
        <div className="header-content">
          {/* Hamburger menu button - only visible on mobile */}
          {isLoggedIn && onToggleSidebar && (
            <button
              className="hamburger-menu-button"
              onClick={onToggleSidebar}
              aria-label="Toggle menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          )}
          <div className="header-brand">
            <RocketLogo className="header-logo" />
            <span className="header-brand-name">Infinity Rockets</span>
          </div>
          <div className="header-auth">
            {isLoggedIn ? (
              <>
                {/* Notifications Bell */}
                <div className="notifications-container" ref={notificationsRef}>
                  <button
                    className="notifications-button"
                    onClick={() => setShowNotifications(!showNotifications)}
                    aria-label="Notifications"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                    {unreadCount > 0 && (
                      <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                    )}
                  </button>

                  {showNotifications && (
                    <div className="notifications-dropdown">
                      <div className="notifications-header">
                        <h3>Notifications</h3>
                        {unreadCount > 0 && (
                          <button className="mark-all-read" onClick={markAllAsRead}>
                            Mark all as read
                          </button>
                        )}
                      </div>

                      <div className="notifications-list">
                        {notifications.length === 0 ? (
                          <div className="notification-empty">
                            <div className="notification-empty-icon">🔔</div>
                            <p>No notifications</p>
                            <span>You're all caught up!</span>
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`notification-item ${!notification.read ? 'unread' : ''}`}
                              onClick={() => handleNotificationClick(notification)}
                            >
                              <div className="notification-icon">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="notification-content">
                                <div className="notification-title">{notification.title}</div>
                                <div className="notification-message">{notification.message}</div>
                                <div className="notification-time">
                                  {formatNotificationTime(notification.timestamp)}
                                </div>
                              </div>
                              {!notification.read && <div className="notification-dot"></div>}
                            </div>
                          ))
                        )}
                      </div>

                      {notifications.length > 0 && (
                        <div className="notifications-footer">
                          <button 
                            className="view-all-notifications"
                            onClick={() => {
                              if (onNavigate) onNavigate('settings');
                              setShowNotifications(false);
                            }}
                          >
                            View all notifications
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Chat Button */}
                <button
                  className="chat-button"
                  onClick={() => chatService.openChat()}
                  aria-label="Open Chat"
                  title="Community Chat"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Profile Dropdown */}
                <div className="profile-dropdown-container" ref={dropdownRef}>
                <button
                  className="profile-button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  aria-label="User menu"
                >
                  {userProfile?.photoUrl ? (
                    <img src={userProfile.photoUrl} alt="Profile" className="profile-avatar" />
                  ) : (
                    <div className="profile-avatar-placeholder">
                      {getInitials()}
                    </div>
                  )}
                  <span className="profile-name">{getDisplayName()}</span>
                  <svg
                    className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                  >
                    <path d="M6 8L2 4h8z" />
                  </svg>
                </button>

                {showDropdown && (
                  <div className="profile-dropdown">
                    <div className="dropdown-header">
                      <div className="dropdown-user-info">
                        {userProfile?.photoUrl ? (
                          <img src={userProfile.photoUrl} alt="Profile" className="dropdown-avatar" />
                        ) : (
                          <div className="dropdown-avatar-placeholder">
                            {getInitials()}
                          </div>
                        )}
                        <div className="dropdown-user-details">
                          <div className="dropdown-name">{getDisplayName()}</div>
                          <div className="dropdown-email">{userProfile?.email || 'user@example.com'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="dropdown-divider"></div>

                    <div className="dropdown-menu">
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          if (onNavigate) onNavigate('profile');
                          setShowDropdown(false);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm2.5 1h-5A2.5 2.5 0 003 11.5V14h10v-2.5A2.5 2.5 0 0010.5 9z" />
                        </svg>
                        <span>My Profile</span>
                      </button>

                      <button
                        className="dropdown-item"
                        onClick={() => {
                          if (onNavigate) onNavigate('integrations');
                          setShowDropdown(false);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M6.5 1A1.5 1.5 0 005 2.5V3H3.5A1.5 1.5 0 002 4.5v1A1.5 1.5 0 003.5 7H5v2H3.5A1.5 1.5 0 002 10.5v1A1.5 1.5 0 003.5 13H5v.5A1.5 1.5 0 006.5 15h1A1.5 1.5 0 009 13.5V13h2v.5a1.5 1.5 0 001.5 1.5h1a1.5 1.5 0 001.5-1.5V13h.5a1.5 1.5 0 000-3H14V9h.5a1.5 1.5 0 000-3H14V5h.5a1.5 1.5 0 000-3H14v-.5A1.5 1.5 0 0012.5 0h-1A1.5 1.5 0 0010 1.5V3H9V2.5A1.5 1.5 0 007.5 1h-1zM9 6v4H7V6h2z" />
                        </svg>
                        <span>Integrations</span>
                      </button>

                      <button
                        className="dropdown-item"
                        onClick={() => {
                          if (onNavigate) onNavigate('settings');
                          setShowDropdown(false);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zm0 1a.5.5 0 01.5.5v5a.5.5 0 01-1 0V3a.5.5 0 01.5-.5zm0 9a.75.75 0 100-1.5.75.75 0 000 1.5z" />
                        </svg>
                        <span>Settings</span>
                      </button>

                      <button
                        className="dropdown-item"
                        onClick={handleExportData}
                        title="Download a backup of all your data"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 12L3 7h3V2h4v5h3l-5 5zm-6 2h12v2H2v-2z" />
                        </svg>
                        <span>Export Data</span>
                      </button>

                      <button
                        className="dropdown-item"
                        onClick={() => {
                          window.open('/help', '_blank');
                          setShowDropdown(false);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zm0 1a.5.5 0 01.5.5v5a.5.5 0 01-1 0V3a.5.5 0 01.5-.5zm0 9a.75.75 0 100-1.5.75.75 0 000 1.5z" />
                        </svg>
                        <span>Help & Support</span>
                      </button>
                    </div>

                    <div className="dropdown-divider"></div>

                    <div className="dropdown-menu">
                      <button className="dropdown-item logout-item" onClick={handleLogoutClick}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M10 2v2h3v8h-3v2h5V2h-5zm-1 10l-1.5-1.5L9 9H2V7h7l-1.5-1.5L9 4l4 4-4 4z" />
                        </svg>
                        <span>Log Out</span>
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </>
            ) : (
              <button className="auth-button login" onClick={onLogin}>
                Log In
              </button>
            )}
          </div>
        </div>
      </header>

      {showDataExportModal && (
        <DataExportModal
          isOpen={showDataExportModal}
          onClose={() => setShowDataExportModal(false)}
        />
      )}
    </>
  );
};

export default Header;
